import {
	createTheme,
	type DefaultMantineColor,
	type MantineColorsTuple,
	type MantineThemeOverride,
} from "@mantine/core";

type ExtendedCustomColors =
	| "primary"
	| "secondary"
	| "info"
	| "warning"
	| "success"
	| "error"
	| DefaultMantineColor;

declare module "@mantine/core" {
	export interface MantineThemeColorsOverride {
		colors: Record<ExtendedCustomColors, MantineColorsTuple>;
	}
}

// 1. Paletas de 10 tons correspondentes às cores dos temas do MUI
const masterGoldPrimary: MantineColorsTuple = [
	"#fdd835", // 0: texto / ícone no dark mode (subtle e light) - Dourado vivo
	"#fce055", // 1
	"#fbc02d", // 2: borda no dark mode (outline)
	"#f9a825", // 3
	"#fdd835", // 4: texto padrão dark mode
	"#ffff72", // 5: light
	"#fdd835", // 6: main filled
	"#fbc02d", // 7: filled hover
	"#c6a700", // 8: dark
	"#4d3d00", // 9: background base dark (usado no variant="light")
];

const masterGoldDarkShades: MantineColorsTuple = [
	"#EDEDED", // 0: text primary
	"#BDBDBD", // 1: text secondary
	"#9E9E9E", // 2: text disabled
	"#5A5A5A", // 3
	"#444444", // 4: divider / border
	"#2E2E2E", // 5
	"#252525", // 6
	"#1E1E1E", // 7: background paper
	"#181818", // 8
	"#121212", // 9: background default
];

const tropicalPrimary: MantineColorsTuple = [
	"#fff3e0",
	"#ffe0b2",
	"#ffcc80",
	"#ffb74d",
	"#fba333", // light
	"#ffa726",
	"#fb8c00", // main (index 6)
	"#f57c00",
	"#ef6c00",
	"#af6200", // dark
];

const intencionPrimary: MantineColorsTuple = [
	"#e8eaf6",
	"#c5cae9",
	"#9fa8da",
	"#7986cb",
	"#4791db", // light
	"#3f51b5",
	"#2b3292", // main (index 6)
	"#283593",
	"#1a237e",
	"#115293", // dark
];

// 2. Paletas de 10 tons correspondentes às cores secundárias do MUI
const masterGoldSecondary: MantineColorsTuple = [
	"#fff8e1",
	"#ffecb3",
	"#ffe082",
	"#ffd54f",
	"#ffca28",
	"#ffd149", // light
	"#ffa000", // main (index 6)
	"#ff8f00",
	"#c67100", // dark
	"#8f4e00",
];

const tropicalSecondary: MantineColorsTuple = [
	"#f3e5f5",
	"#e1bee7",
	"#ce93d8",
	"#ba68c8",
	"#ab47bc",
	"#7c43bd", // light
	"#4a148c", // main (index 6)
	"#38006b",
	"#1b003a", // dark
	"#120024",
];

const intencionSecondary: MantineColorsTuple = [
	"#e0f7fa",
	"#b2ebf2",
	"#80deea",
	"#4dd0e1",
	"#26c6da",
	"#33c9dc", // light
	"#008394", // main (index 6)
	"#006d7c",
	"#005763",
	"#00424b",
];

// 3. Paletas de 10 tons correspondentes às cores de status do MUI (info, warning, success, error)
export const statusInfo: MantineColorsTuple = [
	"#e3f2fd",
	"#bbdefb",
	"#90caf9",
	"#64b5f6",
	"#42a5f5",
	"#29b6f6", // light
	"#218fe9", // main (index 6)
	"#1976d2",
	"#1565c0", // dark
	"#0d47a1",
];

export const statusWarning: MantineColorsTuple = [
	"#fff3e0",
	"#ffe0b2",
	"#ffcc80",
	"#ffb74d",
	"#ffa726",
	"#ff9800", // light
	"#ed6c02", // main (index 6)
	"#ef6c00",
	"#e65100", // dark
	"#bf360c",
];

export const statusSuccess: MantineColorsTuple = [
	"#e8f5e9",
	"#c8e6c9",
	"#a5d6a7",
	"#81c784",
	"#66bb6a",
	"#4caf50", // light
	"#43a047", // main (index 6)
	"#388e3c",
	"#2e7d32", // dark
	"#1b5e20",
];

export const statusError: MantineColorsTuple = [
	"#ffebee",
	"#ffcdd2",
	"#ef9a9a",
	"#e57373",
	"#ef5350",
	"#f44336", // light
	"#e53935", // main (index 6)
	"#d32f2f",
	"#c62828", // dark
	"#b71c1c",
];

// 4. Paletas de 10 tons vibrantes de alto contraste para o Tema Escuro (Master Gold)
// No dark mode, o Mantine usa o index 0 para variant="subtle" e variant="light" (texto/ícone)
// e o index 2 para variant="outline". Por isso os primeiros índices precisam ser vivos e saturados!
export const darkStatusInfo: MantineColorsTuple = [
	"#00b0ff", // 0: texto / ícone no dark mode (subtle e light) - Azul ciano elétrico vivo
	"#00a0e6", // 1
	"#0091ea", // 2: borda no dark mode (outline)
	"#0082d1", // 3
	"#00b0ff", // 4: texto padrão dark mode
	"#0077c2", // 5
	"#0066a6", // 6: main filled
	"#005082", // 7
	"#003b61", // 8
	"#00253d", // 9: background base dark
];

export const darkStatusWarning: MantineColorsTuple = [
	"#ffa000", // 0: texto / ícone no dark mode (subtle e light) - Âmbar / Laranja elétrico vivo
	"#ff9100", // 1
	"#f57c00", // 2: borda no dark mode (outline)
	"#eb6b00", // 3
	"#ffa000", // 4: texto padrão dark mode
	"#e66a00", // 5
	"#cc5500", // 6: main filled
	"#b34700", // 7
	"#8c3300", // 8
	"#4d1a00", // 9: background base dark
];

export const darkStatusSuccess: MantineColorsTuple = [
	"#00e676", // 0: texto / ícone no dark mode (subtle e light) - Verde neon vivo
	"#00d66c", // 1
	"#00c853", // 2: borda no dark mode (outline)
	"#00b348", // 3
	"#00e676", // 4: texto padrão dark mode
	"#00a844", // 5
	"#00963c", // 6: main filled
	"#007830", // 7
	"#005722", // 8
	"#003314", // 9: background base dark
];

export const darkStatusError: MantineColorsTuple = [
	"#ff3355", // 0: texto / ícone no dark mode (subtle e light) - Vermelho vivo elétrico
	"#ff1744", // 1
	"#eb0029", // 2: borda no dark mode (outline)
	"#d60024", // 3
	"#ff3355", // 4: texto padrão dark mode
	"#e60026", // 5
	"#c70020", // 6: main filled
	"#9e0018", // 7
	"#700010", // 8
	"#420008", // 9: background base dark
];

// 5. Tema Master Gold (Modo Escuro / Dourado)
export const mantineMasterGoldTheme: MantineThemeOverride = createTheme({
	fontFamily: "Roboto, sans-serif",
	primaryColor: "primary",
	primaryShade: { light: 6, dark: 6 },
	autoContrast: true,
	colors: {
		primary: masterGoldPrimary,
		secondary: masterGoldSecondary,
		dark: masterGoldDarkShades,
		info: darkStatusInfo,
		warning: darkStatusWarning,
		success: darkStatusSuccess,
		error: darkStatusError,
	},
});

// 5. Tema Tropical Energy (Modo Claro / Laranja)
export const mantineTropicalTheme: MantineThemeOverride = createTheme({
	fontFamily: "Roboto, sans-serif",
	primaryColor: "primary",
	primaryShade: { light: 6, dark: 6 },
	colors: {
		primary: tropicalPrimary,
		secondary: tropicalSecondary,
		info: statusInfo,
		warning: statusWarning,
		success: statusSuccess,
		error: statusError,
	},
});

// 6. Tema Intencion (Modo Claro / Azul)
export const mantineIntencionTheme: MantineThemeOverride = createTheme({
	fontFamily: "Roboto, sans-serif",
	primaryColor: "primary",
	primaryShade: { light: 6, dark: 6 },
	colors: {
		primary: intencionPrimary,
		secondary: intencionSecondary,
		info: statusInfo,
		warning: statusWarning,
		success: statusSuccess,
		error: statusError,
	},
});

export type AppThemeScheme = "masterGold" | "tropical" | "intencion";

export const mantineThemes: Record<AppThemeScheme, MantineThemeOverride> = {
	masterGold: mantineMasterGoldTheme,
	tropical: mantineTropicalTheme,
	intencion: mantineIntencionTheme,
};

